/**
 * templateCatalog.ts — what the template catalogue MEANS, with nothing that talks to a server.
 *
 * The split mirrors `api.ts` / `boardSlots.ts`: `templates.ts` fetches rows, this file folds
 * them into the answers two screens depend on — which version an admin may edit, and whether
 * a document is sitting on wording that has since been superseded. Both answers are
 * load-bearing and neither can be checked against the database from a unit test, so they live
 * where a test can reach them without a Supabase client in the module graph.
 *
 * ── THE ONE RULE EVERYTHING HERE SERVES ──────────────────────────────────────
 * `agreement_create` pins the version that is PUBLISHED at the moment a document is
 * generated onto `agreement_documents.template_id`, and that column never moves. Publishing
 * a new version therefore changes what the NEXT document says and nothing whatsoever about a
 * document already generated — for those, void-and-reissue is the only path to new wording.
 */

import type { AgreementDocType, AgreementParty } from './constants';

// ─── Shapes ──────────────────────────────────────────────────────────────────

export type AgreementTemplateStatus = 'draft' | 'published' | 'retired';

/** One entry of `agreement_templates.parties` — the signing roster, in order. */
export interface AgreementTemplateParty {
    party: AgreementParty;
    role_label?: string | null;
    sign_order?: number | null;
    signer_source?: string | null;
}

/**
 * One template row WITHOUT its body.
 *
 * The bodies are thousands of lines of legal markdown apiece, and the catalogue is read on
 * every signing board for the version hint — so the list query deliberately leaves `body_md`
 * on the server and the editor fetches the one body it is about to show.
 */
export interface AgreementTemplateSummary {
    id: number;
    template_key: string;
    version: number;
    doc_type: AgreementDocType;
    title: string;
    body_sha256: string;
    status: AgreementTemplateStatus;
    parties: AgreementTemplateParty[];
    merge_fields: string[];
    effective_at: string | null;
    retired_at: string | null;
    created_at: string;
    updated_at: string;
}

/** One template row WITH its body — what the editor and the preview run on. */
export interface AgreementTemplateBody extends AgreementTemplateSummary {
    body_md: string;
}

// ─── The six families ────────────────────────────────────────────────────────

/**
 * The template keys, in the order an admin thinks about them: the two documents every
 * contract needs, then the four that depend on what the contract carries.
 *
 * A key that is NOT in this list still gets a card — a later migration adding a seventh
 * template must never make it invisible here — it simply sorts last.
 *
 * `insurance_payment` SITS DIRECTLY BEHIND `insurance_loan` [D-B45], and being in the list at all
 * is the point: the two are the same year's cover recorded two ways — advanced, or paid in full —
 * so an admin looking for one is looking where the other is. Left out, the new family would fall
 * to the `index === -1` branch and sort behind every key this app knows, which is the one place
 * nobody would look for it.
 *
 * `insurance_loan_at_end` SITS BETWEEN THEM [D-B57], and it is the same argument a third time: an
 * advance with no weekly recovery, on a contract whose hire purchase agreement leaves no room for
 * one, is settled at the end of the contract and is published on a page of its own. It carries the
 * Advance's own title, so an admin reading the registry sees two rows called Insurance Premium
 * Advance — which is why it sits directly behind the family it is a second arrangement of rather
 * than wherever the unknown-key branch would have put it.
 *
 * `partner_vehicle_schedule` SITS THIRD, WITH THE OTHER TWO CONTRACT-ARC INSTRUMENTS [D-B48]. Its
 * subject is the contract, it is signed on the agreements area, and it is the document the hire
 * purchase agreement waits for — so it belongs beside the two every contract needs rather than
 * among the four that depend on what a contract carries. THE TERMS BOOKLET IS NOT A MEMBER AND
 * NEVER WILL BE: `hire_fleet_management` is an `agreement_terms` key, a separately published
 * unsigned document this catalogue does not read at all.
 */
export const TEMPLATE_KEY_ORDER: readonly string[] = [
    'hire_purchase',
    'witness',
    'partner_vehicle_schedule',
    'deposit',
    'loan',
    'insurance_loan',
    'insurance_loan_at_end',
    'insurance_payment',
];

export interface TemplateFamily {
    key: string;
    docType: AgreementDocType | null;
    /**
     * The newest version's own published title.
     *
     * THE DOC-TYPE LABEL USED TO OUTRANK IT, AND [D-B45] REVERSED THAT. `insurance_loan` and
     * `insurance_payment` are two families on ONE `doc_type`, so a label read from the type would
     * print "Insurance Premium Advance" on both cards and leave an admin choosing between two rows
     * with the same name. A title is per template_key by construction, which is exactly the grain
     * this list is drawn at. For the four families whose doc type publishes one title the two
     * answers are the same string — verified against all 22 catalogue rows — so nothing else moved.
     */
    label: string;
    /** The version `agreement_create` pins today. Null while a key has never been published. */
    published: AgreementTemplateSummary | null;
    /** The single editable version, if one is open. */
    draft: AgreementTemplateSummary | null;
    /** Every version of this key, newest first. */
    versions: AgreementTemplateSummary[];
}

/**
 * Folds the flat catalogue into one family per template key.
 *
 * ONE PUBLISHED AND ONE DRAFT AT MOST. The published side is guaranteed by a partial unique
 * index; the draft side is a convention of the create-draft RPC rather than an index, so
 * `draft` takes the highest-versioned draft rather than assuming there is exactly one.
 */
export function groupTemplates(rows: AgreementTemplateSummary[]): TemplateFamily[] {
    const families = new Map<string, TemplateFamily>();

    for (const row of rows) {
        let family = families.get(row.template_key);
        if (!family) {
            family = {
                key: row.template_key,
                docType: row.doc_type ?? null,
                label: row.title,
                published: null,
                draft: null,
                versions: [],
            };
            families.set(row.template_key, family);
        }
        family.versions.push(row);
        if (row.status === 'published') family.published = row;
        if (row.status === 'draft' && (!family.draft || row.version > family.draft.version)) {
            family.draft = row;
        }
    }

    for (const family of families.values()) {
        family.versions.sort((a, b) => b.version - a.version);
        // The NEWEST version's title, once the sort has settled which that is: a family being
        // re-cut under a new name should be listed under the name it is about to publish, not the
        // one it is retiring. See `TemplateFamily.label` for why the doc-type label lost this.
        const newest = family.versions[0];
        if (newest) family.label = newest.title;
    }

    const rank = (key: string) => {
        const index = TEMPLATE_KEY_ORDER.indexOf(key);
        return index === -1 ? TEMPLATE_KEY_ORDER.length : index;
    };

    return [...families.values()].sort(
        (a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key),
    );
}

// ─── The signing board's version hint ────────────────────────────────────────

export interface TemplateVersionGap {
    templateKey: string;
    /** The version this document was generated from and will keep. */
    pinnedVersion: number;
    /** The version a document generated now would carry. */
    publishedVersion: number;
}

/**
 * Whether a newer version has been published since a document pinned its template.
 *
 * Returns null in every ambiguous case — an unknown template id, a key with nothing
 * published, a document already on the published version, or a published version somehow
 * OLDER than the pinned one. A hint that fires on a document whose wording is current is
 * worse than no hint: the only remedy it can point at is voiding a document and starting the
 * signing over.
 */
export function newerPublishedVersion(
    templateId: number | null | undefined,
    rows: AgreementTemplateSummary[],
): TemplateVersionGap | null {
    if (templateId === null || templateId === undefined) return null;

    const pinned = rows.find((row) => row.id === templateId);
    if (!pinned) return null;

    const published = rows.find(
        (row) => row.template_key === pinned.template_key && row.status === 'published',
    );
    if (!published || published.version <= pinned.version) return null;

    return {
        templateKey: pinned.template_key,
        pinnedVersion: pinned.version,
        publishedVersion: published.version,
    };
}

// ─── The signing board's page hint ───────────────────────────────────────────

export interface InstrumentKeyGap {
    /** The `template_key` this document was generated on, and will keep. */
    pinnedKey: string;
    /** The key `agreement_create` would pick for this subject if it ran now. */
    expectedKey: string;
    /** `agreement_templates.title` of each, for a surface that would rather print a name. */
    pinnedTitle: string;
    expectedTitle: string;
}

/**
 * Whether this document's pinned PAGE is no longer the page its subject calls for [D-B57].
 *
 * IT IS THE VERSION HINT'S SIBLING, ONE AXIS ACROSS. `newerPublishedVersion` above asks whether the
 * same family has been re-cut since; this asks whether the family itself is still the right one —
 * the case `agreement_create`'s three-way insurance selector opened, where a loan that loses or
 * gains an instalment row moves the page the SAME `doc_type` would now be generated on. The remedy
 * is the same existing overflow action, which is why the two are shaped alike and neither is a gate.
 *
 * NULL IN EVERY AMBIGUOUS CASE, and one of them is new. An unknown pin, an absent `expectedKey` —
 * a caller that could not work out what the subject calls for — and a document already on the right
 * key all answer null for the sibling's reasons. The fourth is that NOTHING IS PUBLISHED UNDER THE
 * EXPECTED KEY: `agreement_create` selects by key and refuses with `AGREEMENT_TEMPLATE_MISSING`
 * when the row is not there, so a hint raised in that window would be telling an admin to void a
 * valid document and reissue it into a failure. A hint whose only remedy fails is worse than none.
 *
 * THE EXPECTED KEY IS NOT CHECKED AGAINST THE PINNED ROW'S `doc_type`, deliberately: the three
 * insurance pages share one, and a caller is the only thing that knows which subject it asked
 * about. What this function owns is the registry half — is the pin real, is the target raisable.
 */
export function instrumentKeyGap(
    templateId: number | null | undefined,
    rows: AgreementTemplateSummary[],
    expectedKey: string | null | undefined,
): InstrumentKeyGap | null {
    if (templateId === null || templateId === undefined) return null;
    if (!expectedKey) return null;

    const pinned = rows.find((row) => row.id === templateId);
    if (!pinned || pinned.template_key === expectedKey) return null;

    const expected = rows.find(
        (row) => row.template_key === expectedKey && row.status === 'published',
    );
    if (!expected) return null;

    return {
        pinnedKey: pinned.template_key,
        expectedKey,
        pinnedTitle: pinned.title,
        expectedTitle: expected.title,
    };
}

// ─── Specimen merge data ─────────────────────────────────────────────────────

/**
 * The values the template preview merges — SPECIMEN DATA, AND NOTHING ELSE.
 *
 * No contract is involved in editing a template, so there is nothing real to merge and the
 * preview must not pretend otherwise: every person, vehicle and figure below is invented,
 * and the names carry the word "Specimen" so a screenshot of that pane can never be mistaken
 * for a record. The preview also renders with merge highlighting permanently on, which tints
 * every substituted run, and carries a banner saying as much.
 *
 * THE KEYS ARE THE CONTRACT WITH SQL. This map is the exact key set `agreement_merge_data`
 * emits, in the formats it emits them in — money as `120,000.00` with no currency prefix (the
 * templates print the unit themselves), rates with trailing zeros trimmed, dates as
 * `FMMonth FMDD, YYYY`. Matching the shape is what makes the preview a useful check on line
 * breaks and column widths rather than a decoration. It doubles as the editor's universe of
 * resolvable fields: a `{{field}}` absent from these keys is one no document will ever
 * resolve, which is what the editor flags.
 */
export const SPECIMEN_MERGE_DATA: Readonly<Record<string, string>> = {
    'document.agreement_date': 'January 1, 2026',

    // The company block is genuine: it is constant in SQL and is not a specimen of anything.
    'company.lender_name': 'Jpad Prime Lane Ltd',
    'company.lender_address': '21 Ataa K. Obuobisa Street, Haatso, Ghana',
    'company.lender_contact': '0599199199',

    'driver.full_name': 'Ama Specimen',
    'driver.address': '1 Specimen Street, Accra',
    'driver.phone': '0200000000',

    'borrower.full_name': 'Ama Specimen',
    'borrower.address': '1 Specimen Street, Accra',
    'borrower.phone': '0200000000',

    'witness.full_name': 'Kofi Specimen',
    'witness.address': '2 Specimen Street, Accra',
    'witness.phone': '0240000000',
    // How the witness knows the hirer — the one field the witness block gained with the schedule
    // and kept. It is optional on the record and it is not optional HERE: a specimen map with a
    // blank in it prints the fill-in rule in the preview and teaches an editor that the field is
    // unresolvable. THE SECOND NUMBER THAT USED TO SIT BESIDE IT IS GONE, body and column alike
    // (witness v2) — an alternative telephone nobody was ever asked for held the witness
    // agreement's whole merge pre-flight open on a fill-in no admin could fill.
    'witness.relationship': 'Brother',

    'vehicle.model': 'Specimen Motors Sample',
    'vehicle.registration': 'GX 0000-00',
    'vehicle.mileage': '120,000',
    // The schedule identifies the car by more than its plate: a year and a colour are what a
    // recovery agent or an insurer matches against.
    'vehicle.year_of_manufacture': '2018',
    'vehicle.colour': 'Silver',

    // THE TWO PRICES, AND THE GAP BETWEEN THEM IS THE POINT. The cash price is what the vehicle
    // could have been bought for outright; the hire-purchase price is what the deposit and the
    // instalments add up to, and the difference is the charge for credit the Schedule states one
    // row apart: a 50,000.00 charge for credit here. A specimen that repeated one number in both
    // rows would print a preview claiming this company finances vehicles for nothing, which is the
    // one shape the resolver deliberately cannot produce: cash_price is read from its own column
    // and never derived from the price beside it.
    //
    // AND IT IS THE PARTNER'S CASH PRICE TOO [D-B59]. The Vehicle Schedule states the contract's
    // cash price, copied onto its record at generation, so this figure and `schedule.all_in_cost`
    // below are one number in two keys: 70,000.00.
    'contract.cash_price': '70,000.00',
    'contract.selling_price': '120,000.00',
    'contract.deposit_amount': '10,000.00',
    'contract.weekly_instalment': '1,000.00',
    // The term and the completion date the schedule prints are DERIVED, not stored: the merge
    // query amortises the price less the deposit over the weekly figure by the engine's own
    // floor-and-subtract, then dates instalment #N at the anchor plus N-1 weeks. The specimen
    // holds that arithmetic rather than inventing round numbers — 120,000 less 10,000 over 1,000
    // is 110 weeks exactly, and 109 weeks after a Monday, January 5 anchor is Monday, February 7,
    // 2028. A preview whose term did not follow its own money would teach the wrong shape.
    'contract.term_weeks': '110',
    'contract.expected_completion_date': 'February 7, 2028',
    'contract.overdue_interest_rate': '5',
    // The same rate stated the way the schedule states it — per DAY rather than the per ANNUM
    // figure the annual key carries. It is a PERCENTAGE and never a cedi amount, and it carries no
    // `%` for the same reason the annual key above does not: the templates print the word.
    //
    // FOUR DECIMAL PLACES, AND THE PLACES ARE THE POINT. Three schedules make the printed daily
    // figure the OPERATIVE one, so it has to equal what the overdue engine charges: `FM990.0000`
    // with trailing zeros and a trailing point trimmed, which is 5/365 = '0.0137' for the annual
    // rate above. At two places a 400 per cent year would print 1.1 against a charged 1.0959 — a
    // different arrears figure on a signed page.
    'contract.overdue_daily_rate': '0.0137',
    // The instrument's own number, as the schedule's header prints it. Dotted, not sequential:
    // it is a reference an admin reads back over a phone, not an id.
    'contract.hire_purchase_reference': '100.247.6',
    'contract.hire_purchase_signed_date': 'January 1, 2026',

    // The cover the schedule is a schedule OF — the policy's own window, in the same month-name
    // format every other date on these documents takes. `insurance.` is its own namespace and not
    // a longer `insurance_loan.`: the period is a fact about the policy, and a contract can carry
    // a policy it never borrowed to buy.
    'insurance.period_start': 'January 1, 2026',
    'insurance.period_end': 'December 31, 2026',
    // THE THREE KEYS THE PAYMENT RECORD PRINTS [D-B45], in the resolver's own formats: the premium
    // and what was paid through `FM999,999,999,990.00` like every other money key here, the date
    // through `FMMonth FMDD, YYYY` like every other date.
    //
    // THE PREMIUM IS THE SAME 4,000.00 THE ADVANCE KEYS BELOW CARRY, and that is the specimen's
    // whole point rather than a coincidence: there is ONE premium in this invented world, and the
    // two instruments are the two ways a year of cover can be recorded — advanced by Primelane
    // (`insurance_loan.*`), or paid in full by the driver (`insurance.premium_*`). A second figure
    // would teach an editor that the Record prints a different premium from the Advance.
    //
    // AND PAID IN FULL MEANS THE TWO MONEY ROWS ARE EQUAL. The Record's particulars print them one
    // above the other under "Amount advanced by Primelane: nil" — a specimen whose paid figure fell
    // short of its premium would preview a document that contradicts its own operative clause. The
    // payment is dated on the day the cover incepts, which is also the specimen's agreement date:
    // the Record is made after the money arrives, so a paid-on date later than the document's own
    // date is a shape no real Record can have.
    'insurance.premium_amount': '4,000.00',
    'insurance.premium_paid_amount': '4,000.00',
    'insurance.premium_paid_on': 'January 1, 2026',

    // WHAT THE MONEY WAS FOR, IN THE WORDS AN ADMIN TYPED. `loans.description`, trimmed and
    // otherwise untouched — SQL formats money and dates and never edits prose — so this is the one
    // specimen value with no format to match, and the only useful thing it can be is a realistic
    // LENGTH. A two-word placeholder would tell an editor nothing about the row it wraps in.
    'loan.purpose': 'Engine overhaul and four replacement tyres',
    'loan.principal_amount': '5,000.00',
    'loan.interest_rate': '10',
    // THE INTEREST IN CEDIS, BECAUSE THE SCHEDULE PRINTS THREE MONEY ROWS AND SAYS THE THIRD IS
    // THE FIRST TWO ADDED. SQL resolves it as total_repayable less principal_amount — the
    // subtraction and never a second percentage — so the specimen holds the same arithmetic:
    // 5,500.00 less 5,000.00 is 500.00, which is also the 10 per cent the row beside it states.
    // A preview whose three figures did not add up would teach an editor the wrong sentence.
    'loan.interest_amount': '500.00',
    'loan.total_repayable': '5,500.00',
    'loan.weekly_instalment': '250.00',
    // A MONDAY, because the row it prints in reads "…, and every Monday after it". The schedules
    // state the payment day in words, so a specimen date on any other weekday makes the preview
    // contradict itself in the one place an editor is checking the sentence.
    'loan.first_instalment_date': 'January 5, 2026',

    // WHAT THE BORROWER PAID TOWARDS THE PREMIUM, AND IN THIS WORLD IT IS NOTHING [D-B46]. The
    // resolver emits `0.00` on a loan carrying no `insurance_loan_deposits` row — the zero-preserving
    // shape [D-B41] — and 283 of the 308 production insurance loans are in it, so it is the one an
    // editor previewing the Schedule should see.
    //
    // AND NOUGHT IS WHAT KEEPS THIS MAP INTERNALLY TRUE. The amount advanced is the premium LESS
    // this row, so `insurance.premium_amount` and `insurance_loan.principal_amount` are the same
    // 4,000.00 ON THIS ROW'S VALUE ALONE. A specimen deposit of, say, 1,000.00 beside an unchanged
    // 4,000.00 advance would preview a Schedule whose own subtraction does not hold — the exact
    // teaching failure the two prices and the three loan rows above are spelled out to avoid. Move
    // this figure and the principal below has to move with it.
    //
    // WHAT THIS ROW BUYS IS THE WARNING, NOT THE VALUE: it is the key's entry in the editor's
    // universe, so it stops `unresolvableMergeFields` flagging the field — it does NOT make the key
    // resolve. Until `agreement_merge_data` v[D-B46] is promoted, a real document still merges this
    // field to nothing; the specimen only keeps the preview honest ahead of that.
    'insurance_loan.deposit_amount': '0.00',
    'insurance_loan.principal_amount': '4,000.00',
    'insurance_loan.interest_rate': '10',
    // The same closed arithmetic as the cash advance above: 4,400.00 less 4,000.00 is 400.00, and
    // 400.00 is the 10 per cent the rate row states.
    'insurance_loan.interest_amount': '400.00',
    'insurance_loan.total_repayable': '4,400.00',
    'insurance_loan.weekly_instalment': '100.00',
    // A MONDAY, for the reason the cash advance's first repayment is one — the row it prints in
    // reads "…, and every Monday after it".
    'insurance_loan.first_instalment_date': 'January 5, 2026',
    // THE TERM IS DERIVED AND THE SPECIMEN FOLLOWS ITS OWN MONEY. The merge query amortises the
    // TOTAL REPAYABLE — not the principal, because the interest is charged once at the outset and
    // is part of what the weekly repayment repays — over the weekly figure by the engine's
    // floor-and-subtract: 4,400.00 over 100.00 is 44 weeks exactly.
    'insurance_loan.term_weeks': '44',

    'deposit.amount': '10,000.00',
    'deposit.paid_on': 'January 1, 2026',

    // ── THE VEHICLE SCHEDULE [D-B48, D-B59] ─────────────────────────────────────────────────────
    // FOURTEEN KEYS, AND ALL EIGHT DERIVED FIGURES ARE THE SPECIMEN'S OWN ARITHMETIC. This invented
    // world already states a hire-purchase price of 120,000.00, a cash price of 70,000.00, a weekly
    // instalment of 1,000.00 and a first instalment on Monday, January 5, 2026; the schedule prints
    // what a partner receives out of exactly those payments, so every figure below is computed from
    // them rather than invented beside them. A preview whose partner share did not add up to its own
    // weekly instalment would teach an editor the wrong sentence in the one place they are checking.
    //
    // THE PARTNER SEES NO DEPOSIT [D-B59]. The deposit of 10,000.00 above is the driver's paper
    // alone: the partner's base is the whole hire-purchase price, and the partner's term is that
    // price over the weekly instalment, the most the arrangement can run. So the driver's 110-week
    // term and the partner's 120-week maximum are two different keys on purpose.
    //
    //   management fee    24.5 per cent of each payment
    //   partner_weekly    1,000.00 less 24.5%            = 755.00
    //   management_weekly 24.5% of 1,000.00              = 245.00
    //   term_weeks        120,000 / 1,000.00             = 120.00, the exact quotient to two places
    //   completion        120 whole payments, no part-payment left over, so
    //                     January 5, 2026 + (120 − 1) × 7 days = Monday, April 17, 2028
    //   partner_total     120,000 × 75.5%                = 90,600.00
    //                     (119 × 755.00 + 1,000.00 × 75.5% = 89,845.00 + 755.00, the same figure)
    //   net_return        90,600.00 − 70,000.00          = 20,600.00
    //   annualised        (20,600 / 70,000) / (120 / 52) = 12.75 per cent a year (12.7524), over
    //                     the same exact weeks the term prints
    //   recovery_weeks    70,000 / 755.00                = 92.72 (92.7152), inside the 120.00-week term
    //
    // AND THE CASH PRICE IS THE ONE FIGURE CHOSEN RATHER THAN DERIVED: 70,000.00 is what makes the
    // net return, the annualised return and the recovery period the figures they are. Move it and
    // all three move.
    'partner.name': 'Akosua Specimen',
    'partner.address': '3 Specimen Street, Accra',
    // The partner's LOGIN profile phone, in the shape `driver.phone` takes — a Ghanaian national
    // ten-digit form, and a different number from the driver's and the witness's, because it is a
    // different person on the same page.
    'partner.phone': '0270000000',
    // `vehicles.vin` under the name the schedule prints it by. Seventeen characters, the shape a
    // recovery agent or an insurer matches against.
    'vehicle.chassis': 'JTDSPECIMEN000001',
    // THE FEE, IN THE RATE SHAPE ITS NEIGHBOURS USE and NOT '24.50': the resolver trims trailing
    // zeros and then a trailing point (`rtrim(rtrim(to_char(x,'FM990.00'),'0'),'.')`), so a round
    // rate prints whole and a half-point rate keeps its half. No per cent sign — the template
    // prints the word.
    'contract.management_rate': '24.5',
    // The five money keys take `FM999,999,999,990.00`, like every other money key here.
    //
    // `schedule.all_in_cost` IS THE CASH PRICE, UNDER A KEY THAT KEEPS ITS HISTORICAL NAME. The v3
    // schedule prints it under the label "Cash price", and the retired bodies read the same key,
    // so it stays in the editor's universe for both.
    'schedule.all_in_cost': '70,000.00',
    'schedule.partner_weekly': '755.00',
    'schedule.management_weekly': '245.00',
    'schedule.partner_total': '90,600.00',
    'schedule.net_return': '20,600.00',
    // THE PARTNER'S OWN TERM AND COMPLETION [D-B59], printed as the Maximum expected duration and
    // the Latest expected completion. The term is the exact quotient, grouped and to two places,
    // unlike the driver's whole-week term. The date counts whole payments, in `FMMonth FMDD, YYYY`,
    // and falls on a Monday, because instalments fall due on Mondays.
    'schedule.term_weeks': '120.00',
    'schedule.expected_completion_date': 'April 17, 2028',
    // A RATIO RATHER THAN A PERCENTAGE OF SOMETHING, which is why the resolver gives it the wide
    // grouped mask and then the rate-shaped double trim — and resolves NULL rather than printing
    // hash marks for anything wider. Worked over the exact weeks, the hire-purchase price over the
    // weekly instalment, the same quotient the partner's term prints.
    'schedule.annualised_return_pct': '12.75',
    // The exact quotient, the cash price over the partner's weekly share, grouped and to two places
    // like the partner's term. It resolves NULL where it would exceed the partner's term, so a
    // specimen that ran past 120.00 would preview a page no real schedule can produce.
    'schedule.recovery_weeks': '92.72',

    // Signature blocks are presentation-layer and are resolved after hashing on a real
    // document; here they exist so the ink slots print with something in them.
    'signature.driver.name': 'Ama Specimen',
    'signature.driver.date': 'January 1, 2026',
    'signature.driver.phone': '0200000000',
    'signature.driver.address': '1 Specimen Street, Accra',
    'signature.driver.title': 'The Hirer',
    'signature.borrower.name': 'Ama Specimen',
    'signature.borrower.date': 'January 1, 2026',
    'signature.borrower.phone': '0200000000',
    'signature.borrower.address': '1 Specimen Street, Accra',
    'signature.borrower.title': 'The Borrower',
    'signature.witness.name': 'Kofi Specimen',
    'signature.witness.date': 'January 1, 2026',
    'signature.witness.phone': '0240000000',
    'signature.witness.address': '2 Specimen Street, Accra',
    'signature.witness.title': 'The Witness',
    'signature.company_rep.name': 'Kwame Specimen',
    'signature.company_rep.date': 'January 1, 2026',
    'signature.company_rep.phone': '0599199199',
    'signature.company_rep.address': '21 Ataa K. Obuobisa Street, Haatso, Ghana',
    'signature.company_rep.title': 'Director',
};

/**
 * The merge fields a body asks for that no document could ever answer.
 *
 * `agreement_merge_data` builds a fixed map, so a field outside it resolves to the blank
 * fill-in rule on every document forever — a typo (`driver.fullname`) and a genuinely new
 * field look identical from here, and both need the SQL side to change before the wording
 * can rely on them. Flagging is all this can honestly do; blocking a save would make the
 * editor refuse wording that a follow-up migration is about to support.
 */
export function unresolvableMergeFields(fields: readonly string[]): string[] {
    return fields.filter((field) => !(field in SPECIMEN_MERGE_DATA));
}

// ─── Malformed merge fields ──────────────────────────────────────────────────

/**
 * What may sit between the braces, and nothing else — the SAME grammar as `MERGE_PATTERN`'s
 * capture group in `merge.ts`, anchored, and the same grammar again as the one
 * `agreement_template_publish` refuses a body against:
 *
 *     ^\{\{[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*\}\}$
 *
 * EVERY SEGMENT STARTS WITH A LETTER OR AN UNDERSCORE, the one after a dot included. A looser
 * `[A-Za-z0-9_]+` for the trailing segments would accept `{{a.1b}}` here and see it refused at
 * publish, which is the exact disagreement this constant exists to prevent: what the editor
 * accepts has to be what the seal accepts.
 */
const WELL_FORMED_PATH = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/;

/** Every `{{…}}` that closes without another brace inside it — what the SQL token scan sees. */
const WELL_CLOSED = /\{\{[^{}]*\}\}/;

/** A brace run left standing after the strip: `{{` or `}}`, the two SQL refuses on. */
const BRACE_RUN = /\{\{|\}\}/;

/**
 * Both patterns above are cloned WITH the `g` flag at the point of use rather than carrying
 * it: a global regex keeps `lastIndex` between calls, and one shared across two loops would
 * resume mid-body.
 */

/** Filler for the strip. Anything that is not a brace does — the residue is never read as text. */
const STRIP_FILL = ' ';

/** How much of an offending run to quote back. Enough to find it, short enough to read. */
const DANGLING_EXCERPT = 24;

function danglingExcerpt(rest: string): string {
    const line = rest.split('\n', 1)[0].trimEnd();
    return line.length > DANGLING_EXCERPT ? `${line.slice(0, DANGLING_EXCERPT)}…` : line;
}

/**
 * Every `{{…}}` in a body that is NOT a merge field, however much it looks like one.
 *
 * A DIFFERENT DEFECT FROM `unresolvableMergeFields`, and the distinction is the whole point.
 * An unresolvable field is spelled correctly and merges — to a blank — because no merge query
 * answers it; a follow-up migration can make it work. A MALFORMED one never merges at all:
 * `agreement_render` substitutes with `replace(body, '{{' || key || '}}', value)`, a literal
 * string swap with no parser behind it, so `{{ driver.full_name }}`, `{{driver.full name}}`
 * and `{{}}` are text the database copies through untouched. What it copies through is hashed,
 * frozen, and printed on the instrument a driver signs — braces and all. No merge data fixes
 * one; only editing the template does.
 *
 * ── THE SAME TWO PASSES `agreement_template_publish` RUNS, IN THE SAME ORDER ──
 * This function and that gate have to agree on every body, or the editor waves a template
 * through and the seal refuses it (or, far worse, the reverse). So:
 *
 *   PASS 1 — every well-closed `{{…}}` whose inner text is not `ident(.ident)*` is an offender,
 *            quoted verbatim. Identical to the SQL scan, down to the grammar.
 *   PASS 2 — every well-closed run is then STRIPPED out of a working copy, collapsed to the
 *            bare brace pair it was written with, and any `{{` or `}}` still standing is an
 *            offender too. This is what catches an unclosed `{{`, a stray `}}`, and the
 *            degenerate cases a token scan structurally cannot see: `{{{a}}}` and `{{a}}}}`
 *            each carry ONE well-closed, correctly spelled token, so pass 1 finds nothing and
 *            a count of openers against closers balances — while `replace` finds `{{a}}`
 *            inside them all the same and prints `{value}` and `value}}` on the signed page.
 *            The stub is what keeps ADJACENCY visible; deleting the run outright would hide it.
 *
 * SQL strips only the VALID tokens, this strips every well-closed run — the same thing whenever
 * the answer can differ, because a body with an invalid well-closed run is already refused by
 * pass 1 on both sides. Stripping them here as well is what stops one defect being reported
 * twice, once by name and once as a brace run.
 *
 * A LONE brace the wording actually wants (`GHS { }`) is not an offender on either side: it is
 * text, it prints as text, and nothing about it is a substitution that failed.
 *
 * Offenders come back verbatim and deduplicated in first-use order: the fix is to find that
 * exact text in the source, and a normalised path would hide which of two spellings on the
 * page is the broken one. `agreement_template_publish` refuses a body carrying any of them
 * with `AGREEMENT_TEMPLATE_MALFORMED_FIELD` — publish, not save, because a half-written draft
 * is allowed to be half-written. Flagging here is what stops an admin meeting that refusal at
 * the end of a page of wording instead of while writing the line.
 */
export function malformedMergeFields(body: string): string[] {
    const malformed: string[] = [];
    const add = (run: string) => {
        if (run.length > 0 && !malformed.includes(run)) malformed.push(run);
    };

    // ── Pass 1, which also builds the working copy ──────────────────────────────────────
    // Each well-closed run is replaced by `{` + filler + `}` of the SAME LENGTH, so an offset
    // in the residue is an offset in the body and pass 2 can quote the original text back.
    let residue = '';
    let cursor = 0;
    const closed = new RegExp(WELL_CLOSED.source, 'g');
    for (let match = closed.exec(body); match; match = closed.exec(body)) {
        const run = match[0];
        if (!WELL_FORMED_PATH.test(run.slice(2, -2))) add(run);
        residue += body.slice(cursor, match.index) + `{${STRIP_FILL.repeat(run.length - 2)}}`;
        cursor = match.index + run.length;
    }
    residue += body.slice(cursor);

    // ── Pass 2 ─────────────────────────────────────────────────────────────────────────
    // The excerpt stops at the next brace in the RESIDUE — that is, at the edge of the next
    // construct — for the reason the dedup exists: quoting a good field back as part of a bad
    // one would send an admin to fix the line that is already correct.
    const runs = new RegExp(BRACE_RUN.source, 'g');
    for (let match = runs.exec(residue); match; match = runs.exec(residue)) {
        const brace = residue[match.index];
        let end = match.index + 2;
        while (end < residue.length && residue[end] === brace) end++;
        let stop = end;
        while (stop < residue.length && residue[stop] !== '{' && residue[stop] !== '}') stop++;
        add(danglingExcerpt(body.slice(match.index, stop)));
        runs.lastIndex = end;
    }

    return malformed;
}
